/**
 * useFamilyCalendarCounts Hook
 *
 * Efficiently loads calendar counts for all family members.
 * Returns a Map of memberId -> calendar count.
 */

import { useState, useEffect, useCallback } from 'react';
import { useCalendarMemberMappingStore } from './use-scoped-managers';
import { CalendarMappingEvents } from '@/lib/calendar/CalendarMappingEvents';

type UseFamilyCalendarCountsResult = {
  counts: Map<string, number>;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  getCount: (memberId: string) => number;
};

export function useFamilyCalendarCounts(memberIds: string[]): UseFamilyCalendarCountsResult {
  const store = useCalendarMemberMappingStore();
  const [counts, setCounts] = useState<Map<string, number>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadCounts = useCallback(async () => {
    if (!store) {
      return;
    }

    if (memberIds.length === 0) {
      setCounts(new Map());
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Get all mappings in one call
      const allMappings = await store.getAll();

      // Count calendars per member
      const newCounts = new Map<string, number>();

      // Initialize all members with 0
      for (const memberId of memberIds) {
        newCounts.set(memberId, 0);
      }

      // Count occurrences in mappings
      for (const mapping of allMappings) {
        for (const memberId of mapping.memberIds) {
          if (newCounts.has(memberId)) {
            newCounts.set(memberId, (newCounts.get(memberId) || 0) + 1);
          }
        }
      }

      setCounts(newCounts);
    } catch (err) {
      console.error('Error loading calendar counts:', err);
      setError(err instanceof Error ? err.message : 'Failed to load calendar counts');
    } finally {
      setIsLoading(false);
    }
  }, [store, memberIds]);

  useEffect(() => {
    if (store) {
      loadCounts();
    }
  }, [store, loadCounts]);

  // Subscribe to calendar-member mapping changes from other screens
  useEffect(() => {
    const unsubscribe = CalendarMappingEvents.subscribe(() => {
      loadCounts();
    });
    return unsubscribe;
  }, [loadCounts]);

  const getCount = useCallback((memberId: string): number => {
    return counts.get(memberId) || 0;
  }, [counts]);

  return {
    counts,
    isLoading,
    error,
    refresh: loadCounts,
    getCount,
  };
}
